// ==================== WHITEPAPERS SERVICE ====================

import { apiService } from './api';
import { getApiUrl } from '../constants';
import type { ApiResponse, PaginatedResponse } from '../types';

// ==================== TYPES ====================

export type WhitepaperAccessAction = 'download' | 'read';

export interface WhitepaperAccessRequest {
  fullName: string;
  email: string;
  phone: string;
  company?: string;
  action: WhitepaperAccessAction;
  /** Honeypot — always empty for real visitors */
  website?: string;
}

export interface WhitepaperAccessGrant {
  token: string;
  expiresAt: string;
}

export interface WhitepaperLead {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  company: string | null;
  downloadCount: number;
  readCount: number;
  whitepaperIds: string[];
  userAgent: string | null;
  lastAccessedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface WhitepaperLeadStatistics {
  totalLeads: number;
  totalDownloads: number;
  totalReads: number;
  newLast7Days: number;
}

export interface WhitepaperLeadListQuery {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'createdAt' | 'lastAccessedAt' | 'fullName' | 'downloadCount' | 'readCount';
  sortOrder?: 'asc' | 'desc';
}

// ==================== SERVICE ====================

export const whitepapersService = {
  /**
   * Submit the lead form and receive a short-lived token for the PDF
   */
  requestAccess: async (
    whitepaperId: string,
    data: WhitepaperAccessRequest,
  ): Promise<ApiResponse<WhitepaperAccessGrant>> => {
    return apiService.post<WhitepaperAccessGrant>(
      `/public/whitepapers/${encodeURIComponent(whitepaperId)}/access`,
      data,
    );
  },

  /**
   * Absolute URL of the PDF for a granted token (opened directly by the browser)
   */
  getFileUrl: (whitepaperId: string, token: string): string => {
    return `${getApiUrl()}/public/whitepapers/${encodeURIComponent(whitepaperId)}/file?token=${encodeURIComponent(token)}`;
  },

  // ==================== ADMIN ====================

  /**
   * Get whitepaper leads (Super Admin)
   */
  getLeads: async (
    query?: WhitepaperLeadListQuery,
  ): Promise<ApiResponse<PaginatedResponse<WhitepaperLead>>> => {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.search) params.append('search', query.search);
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);

    const queryString = params.toString();
    return apiService.get<PaginatedResponse<WhitepaperLead>>(
      `/admin/whitepaper-leads${queryString ? `?${queryString}` : ''}`,
    );
  },

  /**
   * Get whitepaper lead statistics (Super Admin)
   */
  getLeadStatistics: async (): Promise<ApiResponse<WhitepaperLeadStatistics>> => {
    return apiService.get<WhitepaperLeadStatistics>('/admin/whitepaper-leads/statistics');
  },
};

export default whitepapersService;
